import type { AppConfig } from "./getAppConfig"

const MARK_ATTR = "data-app-head"

const join = (base: string, file: string) =>
    base.replace(/\/$/, "") + "/" + file.replace(/^\//, "")

const escapeAttrValue = (value: string) => {
    return typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(value)
        : value.replace(/["\\]/g, "\\$&")
}

type LinkMatch = Readonly<{
  rel: string;
  sizes?: string;
  type?: string;
}>;

type LinkAttrs = Readonly<{
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
}>;

function buildLinkSelector(match: LinkMatch): string {
    const parts: string[] = [`link[rel="${escapeAttrValue(match.rel)}"]`]

    if (match.sizes) {
        parts.push(`[sizes="${escapeAttrValue(match.sizes)}"]`)
    }
    if (match.type) {
        parts.push(`[type="${escapeAttrValue(match.type)}"]`)
    }

    return parts.join("")
}

function setAttr(el: Element, name: string, value?: string): void {
    if (value == null) return
    el.setAttribute(name, value)
}

function upsertLink(match: LinkMatch, attrs: LinkAttrs): void {
    const selector = buildLinkSelector(match)
    const existing = document.head.querySelector<HTMLLinkElement>(selector)

    const el = existing ?? document.createElement("link")

    setAttr(el, "rel", attrs.rel)
    setAttr(el, "href", attrs.href)
    setAttr(el, "sizes", attrs.sizes)
    setAttr(el, "type", attrs.type)

    el.setAttribute(MARK_ATTR, "1")

    if (!existing) document.head.appendChild(el)
}

function upsertMeta(name: string, content: string): void {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${escapeAttrValue(name)}"]`)

    if (!el) {
        el = document.createElement("meta")
        el.setAttribute("name", name)
        document.head.appendChild(el)
    }

    el.setAttribute("content", content)
    el.setAttribute(MARK_ATTR, "1")
}

export function applyHead(cfg: AppConfig): void {
    const head = cfg.head
    if (!head) return

    if (head.title) document.title = head.title

    const v = head.iconVersion ? `?v=${encodeURIComponent(head.iconVersion)}` : ""
    const p = (file: string) => join(head.iconBase, file) + v

    const icons: LinkAttrs[] = [
        { rel: "icon", href: p("favicon.ico") },

        { rel: "apple-touch-icon", sizes: "57x57", href: p("apple-icon-57x57.png") },
        { rel: "apple-touch-icon", sizes: "60x60", href: p("apple-icon-60x60.png") },
        { rel: "apple-touch-icon", sizes: "72x72", href: p("apple-icon-72x72.png") },
        { rel: "apple-touch-icon", sizes: "76x76", href: p("apple-icon-76x76.png") },
        { rel: "apple-touch-icon", sizes: "114x114", href: p("apple-icon-114x114.png") },
        { rel: "apple-touch-icon", sizes: "120x120", href: p("apple-icon-120x120.png") },
        { rel: "apple-touch-icon", sizes: "144x144", href: p("apple-icon-144x144.png") },
        { rel: "apple-touch-icon", sizes: "152x152", href: p("apple-icon-152x152.png") },
        { rel: "apple-touch-icon", sizes: "180x180", href: p("apple-icon-180x180.png") },

        { rel: "icon", type: "image/png", sizes: "192x192", href: p("favicon-192x192.png") },
        { rel: "icon", type: "image/png", sizes: "32x32", href: p("favicon-32x32.png") },
        { rel: "icon", type: "image/png", sizes: "96x96", href: p("favicon-96x96.png") },
        { rel: "icon", type: "image/png", sizes: "16x16", href: p("favicon-16x16.png") },

        { rel: "icon", type: "image/png", sizes: "192x192", href: p("android-icon-192x192.png") },
    ]

    icons.forEach((i) => {
        upsertLink({ rel: i.rel, sizes: i.sizes, type: i.type }, i)
    })

    upsertMeta("msapplication-TileImage", p("ms-icon-144x144.png"))
}
